/** @jsx jsx */

import { useMemo, useState } from 'react';
import type { KeyboardEvent, Ref } from 'react';
import { jsx } from '@balance-web/core';
import type { BalanceThemeRaw } from '@balance-web/theme';
import { useRawTheme } from '@balance-web/theme';
import { getContrastText } from '@balance-web/utils';

import { CalendarCaptionButtons } from './CalendarCaptionButtons';
import {
  findNextEnabledIndex,
  getArrowKeyStep,
  getInitialFocusIndex,
  getMonthOptions,
  getYearOptions,
} from './calendarPeriods';
import type { CalendarPeriod, CalendarPeriodBounds } from './calendarPeriods';

export type CalendarPeriodFrame = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type CalendarPeriodViewProps = CalendarPeriodBounds & {
  ref?: Ref<HTMLDivElement>;
  period: CalendarPeriod;
  anchor: Date;
  frame: CalendarPeriodFrame;
  onCancel: () => void;
  onPeriodClick: (period: CalendarPeriod) => void;
  onSelect: (month: Date) => void;
};

const periodGrids = {
  month: { label: 'Month', columns: 3, getOptions: getMonthOptions },
  year: { label: 'Year', columns: 4, getOptions: getYearOptions },
};

export const CalendarPeriodView = ({
  ref,
  period,
  anchor,
  frame,
  fromMonth,
  toMonth,
  onCancel,
  onPeriodClick,
  onSelect,
}: CalendarPeriodViewProps) => {
  const theme = useRawTheme();
  const { label, columns, getOptions } = periodGrids[period];
  const styles = useMemo(() => getPeriodViewStyles(theme, columns), [
    theme,
    columns,
  ]);

  const [today] = useState(() => new Date());
  const options = getOptions(anchor, { fromMonth, toMonth }, today);
  const [initialFocusIndex] = useState(() => getInitialFocusIndex(options));
  const [focusIndex, setFocusIndex] = useState(initialFocusIndex);

  const handleGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = getArrowKeyStep(event.key, columns);
    if (step === undefined || focusIndex === undefined) {
      return;
    }

    event.preventDefault();
    const nextIndex = findNextEnabledIndex(options, focusIndex, step);
    if (nextIndex === undefined) {
      return;
    }

    setFocusIndex(nextIndex);
    event.currentTarget
      .querySelector<HTMLButtonElement>(`[data-index="${nextIndex}"]`)
      ?.focus();
  };

  return (
    <div
      ref={ref}
      aria-label={`Choose ${period}`}
      css={[styles.view, frame]}
      role="dialog"
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <div
        ref={initialFocusIndex === undefined ? focusExpandedTrigger : undefined}
        css={styles.header}
      >
        <CalendarCaptionButtons
          expandedPeriod={period}
          month={anchor}
          onPeriodClick={onPeriodClick}
        />
      </div>
      <div
        aria-label={label}
        css={styles.grid}
        role="radiogroup"
        onKeyDown={handleGridKeyDown}
      >
        {options.map((option, index) => (
          <button
            key={option.label}
            ref={index === initialFocusIndex ? focusAndCenter : undefined}
            aria-checked={option.selected}
            css={[
              styles.cell,
              option.current && styles.currentCell,
              option.selected && styles.selectedCell,
            ]}
            data-index={index}
            disabled={option.disabled}
            role="radio"
            tabIndex={index === focusIndex ? 0 : -1}
            type="button"
            onClick={() => {
              onSelect(option.month);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

function focusExpandedTrigger(header: HTMLDivElement | null) {
  header?.querySelector<HTMLButtonElement>('[aria-expanded="true"]')?.focus();
}

function focusAndCenter(cell: HTMLButtonElement | null) {
  const grid = cell?.parentElement;
  if (!cell || !grid) {
    return;
  }

  const cellTop =
    cell.getBoundingClientRect().top - grid.getBoundingClientRect().top;
  grid.scrollTop += cellTop - (grid.clientHeight - cell.offsetHeight) / 2;
  cell.focus({ preventScroll: true });
}

function getPeriodViewStyles(theme: BalanceThemeRaw, columns: number) {
  const interactionColor = theme.semantic.colour.primary.background;

  return {
    view: {
      background: theme.semantic.colour.surface.neutral.background,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      padding: `${theme.spacing.small}px 0`,
      position: 'absolute',
    },
    header: {
      alignItems: 'center',
      display: 'flex',
      fontSize: theme.typography.fontSize.medium,
      fontWeight: theme.typography.fontWeight.medium,
      gap: theme.spacing.xsmall,
      height: theme.sizing.xsmall,
      marginBottom: '0.5em',
    },
    grid: {
      alignContent: 'start',
      display: 'grid',
      flex: 1,
      gap: theme.spacing.xsmall,
      gridAutoRows: theme.sizing.base,
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      minHeight: 0,
      overflowY: 'auto',
    },
    cell: {
      appearance: 'none',
      background: 'none',
      border: 0,
      borderRadius: theme.sizing.base / 2,
      color: theme.semantic.colour.text.textPrimary,
      cursor: 'pointer',
      font: 'inherit',
      fontSize: theme.typography.fontSize.small,
      fontWeight: theme.typography.fontWeight.medium,
      margin: 0,
      outline: 0,
      padding: 0,

      ':hover:not(:disabled), &:focus-visible': {
        boxShadow: `inset 0 0 0 2px ${interactionColor}`,
      },
      ':disabled': {
        color: theme.semantic.colour.text.textDisabled,
        cursor: 'default',
      },
    },
    currentCell: {
      color: theme.semantic.colour.text.reserved.critical,
      fontWeight: theme.typography.fontWeight.bold,
    },
    selectedCell: {
      color: getContrastText(interactionColor),

      '&, &:hover, &:focus-visible': {
        backgroundColor: interactionColor,
      },
    },
  } as const;
}
